import { Head, Link, useForm } from "@inertiajs/react"
import { ChevronLeft, AlertTriangle } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type Row = {
  source_path: string | null
  destination: string
  redirect_type: string
  status: "new" | "update" | "skip"
  note: string | null
}

type Analysis = {
  headers: string[]
  source_column: string | null
  destination_column: string | null
  type_column: string | null
  error: string | null
  total: number
  new_count: number
  update_count: number
  skip_count: number
  sample: Row[]
}

export default function RedirectImportPreview({
  analysis,
  token,
  filename,
}: {
  analysis: Analysis
  token: string | null
  filename: string
}) {
  const confirmForm = useForm({ token: token ?? "" })

  const confirm = () => confirmForm.post("/admin/redirect_imports/confirm")

  return (
    <>
      <Head title="Import preview">
        <meta name="description" content="Preview redirect import." />
        <meta property="og:title" content="Import preview" />
        <meta property="og:description" content="Preview redirect import." />
      </Head>
      <AdminShell>
        <Link
          href="/admin/redirect_imports/new"
          className="inline-flex items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          <ChevronLeft className="h-4 w-4" />
          Upload a different file
        </Link>

        <div className="mt-4 border-b border-hairline pb-6">
          <h1>Import preview</h1>
          <p className="mt-1 text-ink-muted">{filename}</p>
        </div>

        {analysis.error ? (
          <div className="callout callout-danger mt-6">
            <p className="flex items-start gap-2 font-medium text-ink-display">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {analysis.error}
            </p>
            <p className="mt-2 text-sm text-ink-muted">
              Columns found: {analysis.headers.length ? analysis.headers.join(", ") : "none"}.
              Make sure the file has a link/source column and a destination column, then try again.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Total rows" value={analysis.total} />
              <Stat label="New" value={analysis.new_count} tone="accent" />
              <Stat label="Update existing" value={analysis.update_count} />
              <Stat label="Skipped" value={analysis.skip_count} tone="muted" />
            </div>

            <p className="mt-4 text-sm text-ink-muted">
              Detected columns — source: <code>{analysis.source_column}</code>, destination:{" "}
              <code>{analysis.destination_column}</code>
              {analysis.type_column ? <>, type: <code>{analysis.type_column}</code></> : <> (no type column — defaulting to 307)</>}.
            </p>

            <div className="mt-6 overflow-x-auto rounded-md border border-hairline">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline bg-surface text-left">
                    <th className="px-4 py-2 font-medium text-ink-muted">Source</th>
                    <th className="px-4 py-2 font-medium text-ink-muted">Destination</th>
                    <th className="px-4 py-2 font-medium text-ink-muted">Type</th>
                    <th className="px-4 py-2 font-medium text-ink-muted">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {analysis.sample.map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2"><code className="text-ink-display">{r.source_path}</code></td>
                      <td className="px-4 py-2 text-ink-muted"><span className="break-all">{r.destination}</span></td>
                      <td className="px-4 py-2 text-ink-muted">{r.redirect_type}</td>
                      <td className="px-4 py-2">
                        {r.status === "new" && <Badge tone="accent">New</Badge>}
                        {r.status === "update" && <Badge tone="neutral">Update</Badge>}
                        {r.status === "skip" && <Badge tone="muted">Skip</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {analysis.total > analysis.sample.length && (
              <p className="mt-2 text-xs text-ink-muted">
                Showing the first {analysis.sample.length} of {analysis.total} rows.
              </p>
            )}

            <div className="mt-8 flex items-center gap-3 border-t border-hairline pt-6">
              <Button onClick={confirm} disabled={confirmForm.processing || !token}>
                {confirmForm.processing
                  ? "Importing…"
                  : `Import ${analysis.new_count + analysis.update_count} redirects`}
              </Button>
              <Link href="/admin/redirects" className="text-sm text-ink-muted no-underline hover:text-ink-display">
                Cancel
              </Link>
            </div>
          </>
        )}
      </AdminShell>
    </>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "accent" | "muted" }) {
  const color =
    tone === "accent" ? "text-accent-display" : tone === "muted" ? "text-ink-muted" : "text-ink-display"
  return (
    <div className="rounded-md border border-hairline bg-page p-4">
      <div className={`font-display text-2xl font-semibold ${color}`}>{value}</div>
      <p className="mt-1 text-xs text-ink-muted">{label}</p>
    </div>
  )
}
